import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { setAccessToken } from '@dia/db';
import { useT } from '@dia/i18n';
import { Button, Screen } from '@dia/ui';
import { useAuth } from '../../auth';
import { forgetCachedReads, supabase } from '../../supabase';
import { pickIdentity } from './identityPick';
import { Page } from './ui';

/**
 * Who is using the app, whichever door they came in by.
 *
 * Crew and hosts arrive with a device token and a PIN, and the staff-login
 * function hands them a session that lives in useAuth(). Owners sign in with
 * Supabase Auth, email and password, in English. This hook folds both into one
 * shape so a screen never has to ask which it is.
 */

export type Identity = {
  kind: 'staff' | 'owner';
  /** The staff_users id. null for an owner who has not been linked to a staff row. */
  staffId: string | null;
  name: string;
  role: 'crew' | 'manager' | 'owner';
  /** Sent as the Bearer token to edge functions. */
  token: string;
  signOut: () => void;
};

/** How long a screen waits for the browser's own auth session before it stops waiting. */
const OWNER_AUTH_PATIENCE_MS = 4000;

type OwnerAuth = { ready: boolean; token: string | null; email: string | null; authId: string | null };

function useOwnerAuth(): OwnerAuth {
  const [state, setState] = useState<OwnerAuth>({ ready: false, token: null, email: null, authId: null });

  useEffect(() => {
    let alive = true;
    const apply = (session: { access_token: string; user: { id: string; email?: string | null } } | null) => {
      if (!alive) return;
      setState({
        ready: true,
        token: session?.access_token ?? null,
        email: session?.user.email ?? null,
        authId: session?.user.id ?? null,
      });
    };
    // The screens wait for `ready`, so it has to arrive. getSession can be
    // slow (it refreshes an expired token over the network and queues behind a
    // lock another tab may hold) or throw, and a screen that waits for an
    // answer that never comes is a blank white page. After a few seconds the
    // answer is "nobody is signed in here", and a real one replaces it if it
    // turns up later.
    const giveUp = window.setTimeout(() => {
      if (alive) setState((current) => (current.ready ? current : { ...current, ready: true }));
    }, OWNER_AUTH_PATIENCE_MS);
    void supabase.auth
      .getSession()
      .then(({ data }) => apply(data.session))
      .catch(() => apply(null));
    const { data } = supabase.auth.onAuthStateChange((_event, session) => apply(session));
    return () => {
      alive = false;
      window.clearTimeout(giveUp);
      data.subscription.unsubscribe();
    };
  }, []);

  return state;
}

/** The linked staff row of an owner, so messages can carry a name. */
function useOwnerRow(authId: string | null): { id: string; name: string } | null {
  const [row, setRow] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    if (!authId) return;
    let alive = true;
    void supabase
      .from('staff_users')
      .select('id, name')
      .eq('auth_user_id', authId)
      .maybeSingle()
      .then(({ data }) => {
        if (alive && data) setRow({ id: data.id, name: data.name });
      });
    return () => {
      alive = false;
    };
  }, [authId]);
  return authId ? row : null;
}

export function ownerSignOut(): Promise<unknown> {
  forgetCachedReads();
  return supabase.auth.signOut();
}

export function useIdentity(): { identity: Identity | null; ready: boolean } {
  const { session, signOut } = useAuth();
  const owner = useOwnerAuth();

  // One person at a time: the most recent sign in wins (see pickIdentity).
  const which = pickIdentity(session?.access_token, owner.token);
  const useStaff = which === 'staff';
  const row = useOwnerRow(which === 'owner' ? owner.authId : null);

  const signOutOwner = useCallback(() => {
    void ownerSignOut();
  }, []);

  useEffect(() => {
    // The loser is a leftover, and it is removed rather than left to come back
    // on the next open. A stale crew session was hiding the owner's screens.
    if (which === 'owner' && session) signOut();
    if (which === 'staff' && owner.token) void supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
    // A staff token in the holder would win over the owner's own on every call.
    if (which === 'owner') setAccessToken(supabase, null);
  }, [which, session, owner.token, signOut]);

  if (useStaff && session) {
    return {
      ready: true,
      identity: {
        kind: 'staff',
        staffId: session.user.id,
        name: session.user.name,
        role: session.user.role,
        token: session.access_token,
        signOut,
      },
    };
  }

  if (which === 'owner' && owner.token) {
    return {
      ready: true,
      identity: {
        kind: 'owner',
        staffId: row?.id ?? null,
        name: row?.name ?? owner.email ?? 'Owner',
        role: 'owner',
        token: owner.token,
        signOut: signOutOwner,
      },
    };
  }

  return { ready: owner.ready, identity: null };
}

/** What every guarded screen shows while it is still finding out who is here. Never nothing. */
export function CheckingWho() {
  const { t } = useT();
  return (
    <Screen title={t('loading')}>
      <p role="status" className="mt-4 text-inkMuted">
        {t('loading')}
      </p>
    </Screen>
  );
}

/** A route guard. Crew screens need any signed in person; the boss screens need a manager or an owner. */
export function RequireAuth({ children, need = 'crew' }: { children: (who: Identity) => ReactNode; need?: 'crew' | 'manager' }) {
  const { t } = useT();
  const { identity, ready } = useIdentity();

  if (!identity) {
    return ready ? <Navigate to="/entrar" replace /> : <CheckingWho />;
  }

  if (need === 'manager' && identity.role === 'crew') {
    return (
      <Page title={t('ops.boss.title')}>
        <p className="mt-4">{t('ops.boss.not_allowed')}</p>
        <Button className="mt-6" block onClick={() => window.history.back()}>
          {t('action.back')}
        </Button>
      </Page>
    );
  }

  return <>{children(identity)}</>;
}
